export type PhaseCue =
  | 'work_start'
  | 'rest_start'
  | 'prewarn'
  | 'complete'
  | 'countdown_3'
  | 'countdown_2'
  | 'countdown_1'

interface CueDescriptor {
  freq: number
  durationMs: number
  vibratePattern: number[]
  beeps?: number
}

const CUES: Record<PhaseCue, CueDescriptor> = {
  work_start: { freq: 880, durationMs: 280, vibratePattern: [280], beeps: 2 },
  rest_start: { freq: 440, durationMs: 220, vibratePattern: [220] },
  prewarn: { freq: 660, durationMs: 120, vibratePattern: [80, 60, 80] },
  complete: { freq: 990, durationMs: 380, vibratePattern: [300, 100, 300], beeps: 3 },
  countdown_3: { freq: 500, durationMs: 120, vibratePattern: [80] },
  countdown_2: { freq: 600, durationMs: 120, vibratePattern: [80] },
  countdown_1: { freq: 720, durationMs: 140, vibratePattern: [100] },
}

let audioCtx: AudioContext | null = null

function ensureContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (audioCtx) return audioCtx
  const AudioCtor: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioCtor) return null
  audioCtx = new AudioCtor()
  return audioCtx
}

/**
 * Must be called from a user gesture to satisfy iOS Safari autoplay rules.
 * Idempotent.
 */
export function primeAudio(): void {
  const ctx = ensureContext()
  if (!ctx) return
  if (ctx.state === 'suspended') ctx.resume().catch(() => undefined)
}

function beep(freq: number, durationMs: number): void {
  const ctx = ensureContext()
  if (!ctx) return
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq
  gain.gain.setValueAtTime(0, ctx.currentTime)
  gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.01)
  gain.gain.linearRampToValueAtTime(0, ctx.currentTime + durationMs / 1000)
  osc.connect(gain).connect(ctx.destination)
  osc.start()
  osc.stop(ctx.currentTime + durationMs / 1000)
}

function vibrate(pattern: number[]): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return
  try {
    navigator.vibrate(pattern)
  } catch {
    // some browsers throw when vibrate is not allowed
  }
}

export function playPhaseCue(cue: PhaseCue): void {
  const descriptor = CUES[cue]
  vibrate(descriptor.vibratePattern)
  const beeps = descriptor.beeps ?? 1
  for (let i = 0; i < beeps; i++) {
    setTimeout(() => beep(descriptor.freq, descriptor.durationMs), i * (descriptor.durationMs + 80))
  }
}
